import { Button, Popover, TextInput } from '@mantine/core';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { useState } from 'react';

export interface CtaOptions {
  /**
   * Whether the template being edited can resolve ⟦povezava⟧. Only the hint
   * under the fields depends on it — a button on a template without the magic
   * link has to point somewhere else, and saying so late is saying it too late.
   */
  allowsPovezava: boolean;
}

/**
 * What a button is made of: a napis and a povezava.
 *
 * Shared by the toolbar control that inserts one and the pill that edits one,
 * so a coordinator meets the same two fields and the same explanation of
 * ⟦povezava⟧ whichever way they arrived.
 */
export function CtaFields({
  label,
  href,
  onLabel,
  onHref,
  allowsPovezava,
}: {
  label: string;
  href: string;
  onLabel: (value: string) => void;
  onHref: (value: string) => void;
  allowsPovezava: boolean;
}) {
  return (
    <>
      <TextInput
        size="xs"
        label="Napis na gumbu"
        value={label}
        onChange={(e) => onLabel(e.currentTarget.value)}
      />
      <TextInput
        size="xs"
        mt={8}
        label="Povezava"
        value={href}
        onChange={(e) => onHref(e.currentTarget.value)}
      />
      <div className="ml-hint" style={{ marginTop: 4 }}>
        {allowsPovezava
          ? '{{povezava}} je osebna povezava prostovoljca do brifinga.'
          : 'Ta predloga ne nosi osebne povezave — vpiši spletni naslov.'}
      </div>
    </>
  );
}

/**
 * The button, as it stands in the letter.
 *
 * It is the one thing in a body that has settings of its own, so the pill is
 * where they are reached: clicking it opens the same two fields the toolbar
 * offers, over the button it belongs to, with the way out beside them. Every
 * other chip in the letter is a token with nothing to configure and nothing
 * but Delete to offer, which is why only this one carries a panel.
 *
 * Edits land on the document as they are typed — the pill, the preview and the
 * unsaved-changes state all follow one keystroke behind, the same as typing in
 * the letter itself.
 */
export function CtaPill({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  extension,
}: NodeViewProps) {
  const [opened, setOpened] = useState(false);
  const label = String(node.attrs.label);
  const href = String(node.attrs.href);

  const toggle = () => {
    /*
     * The pill is a <button>, and Tiptap's node view stops events on form
     * elements before ProseMirror sees them — so the mousedown that would
     * normally select the node never arrives. Select it here instead, or the
     * outline never appears and Delete has nothing to act on.
     *
     * Focus first, in the same chain: ProseMirror only writes the selection to
     * the DOM — the outline included — while the view has it, and the popover
     * takes the focus away the moment it opens.
     */
    const pos = getPos();
    if (typeof pos === 'number') editor.chain().focus().setNodeSelection(pos).run();
    setOpened((o) => !o);
  };

  /*
   * Closing hands the letter back, with the button still selected — so Delete
   * reaches it, and the outline says which one it would take. Only for a
   * deliberate close: `onChange` also fires when the coordinator clicked
   * somewhere else entirely, and taking that click back would be rude.
   */
  const close = () => {
    setOpened(false);
    editor.commands.focus();
  };

  return (
    <NodeViewWrapper className="ml-cta">
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        withArrow
        width={280}
        trapFocus
      >
        {/* The whole pill anchors the panel, so it opens clear of the povezava
            line rather than across it. Only the napis is the control. */}
        <Popover.Target>
          <div className="ml-cta-body">
            <button type="button" className="ml-cta-button" onClick={toggle} title="Uredi gumb">
              {label}
            </button>
            <span className="ml-cta-href">{href}</span>
          </div>
        </Popover.Target>
        <Popover.Dropdown>
          <CtaFields
            label={label}
            href={href}
            onLabel={(value) => updateAttributes({ label: value })}
            onHref={(value) => updateAttributes({ href: value })}
            allowsPovezava={(extension.options as CtaOptions).allowsPovezava}
          />
          <div className="ml-cta-actions">
            <Button
              size="xs"
              variant="light"
              color="red"
              onClick={() => {
                deleteNode();
                editor.commands.focus();
              }}
            >
              Odstrani
            </Button>
            <Button size="xs" onClick={close}>
              Končano
            </Button>
          </div>
        </Popover.Dropdown>
      </Popover>
    </NodeViewWrapper>
  );
}
